import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  acceptInvite,
  cancelInvite,
  createInvite,
  deactivateAdminUser,
  getAdminUsers,
  getInvites,
  reactivateAdminUser,
  resendInvite,
} from '@/api/adminAccess';
import type { InviteAcceptPayload, InvitePayload } from '@/api/types';

const ADMIN_USERS_QUERY_KEY = ['admin-access', 'users'] as const;
const INVITES_QUERY_KEY = ['admin-access', 'invites'] as const;

export function useAdminUsers() {
  return useQuery({
    queryKey: ADMIN_USERS_QUERY_KEY,
    queryFn: getAdminUsers,
  });
}

export function useInvites(status: 'pending' | 'accepted' | 'cancelled' = 'pending') {
  return useQuery({
    queryKey: [...INVITES_QUERY_KEY, status],
    queryFn: () => getInvites({ status }),
  });
}

function invalidateInviteQueries(queryClient: ReturnType<typeof useQueryClient>) {
  return queryClient.invalidateQueries({ queryKey: INVITES_QUERY_KEY });
}

function invalidateAdminUsersQueries(queryClient: ReturnType<typeof useQueryClient>) {
  return queryClient.invalidateQueries({ queryKey: ADMIN_USERS_QUERY_KEY });
}

export function useCreateInvite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: InvitePayload) => createInvite(payload),
    onSuccess: async () => invalidateInviteQueries(queryClient),
  });
}

export function useResendInvite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => resendInvite(id),
    onSuccess: async () => invalidateInviteQueries(queryClient),
  });
}

export function useCancelInvite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => cancelInvite(id),
    onSuccess: async () => invalidateInviteQueries(queryClient),
  });
}

export function useAcceptInvite() {
  return useMutation({
    mutationFn: (payload: InviteAcceptPayload) => acceptInvite(payload),
  });
}

export function useDeactivateAdminUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => deactivateAdminUser(id),
    onSuccess: async () => invalidateAdminUsersQueries(queryClient),
  });
}

export function useReactivateAdminUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => reactivateAdminUser(id),
    onSuccess: async () => invalidateAdminUsersQueries(queryClient),
  });
}